import { createApi } from '@reduxjs/toolkit/query/react';

import { portalQuery } from '@api/api.config';

import { LoginProviderEnum } from '@enum/login-provider.enum';
import { TagTypesEnum } from '@enum/tag-types.enum';
import {
  LoginMFARequestInterface,
  LoginRequestInterface,
  LoginResponseInterface,
  LoginSSOCallbackRequestInterface,
  LoginSSOResponseInterface,
} from '@interface/auth/login.interface';
import { RegistrationRequestInterface, RegistrationResponseInterface } from '@interface/auth/registration.interface';
import { NewPasswordInterface, ResetPasswordInterface } from '@interface/auth/reset-password.interface';
import { ResponseDataInterface } from '@interface/logs/response-data.interface';

export const authApi = createApi({
  reducerPath: 'auth-api',
  tagTypes: [TagTypesEnum.Auth],
  baseQuery: portalQuery('/auth'),
  endpoints: (build) => ({
    login: build.mutation<ResponseDataInterface<LoginResponseInterface>, LoginRequestInterface>({
      query: (credentials) => ({
        method: 'POST',
        url: 'email',
        body: credentials,
      }),
      invalidatesTags: [TagTypesEnum.Auth],
    }),
    registration: build.mutation<ResponseDataInterface<RegistrationResponseInterface>, RegistrationRequestInterface>({
      query: (credentials) => ({
        method: 'POST',
        url: 'register',
        body: credentials,
      }),
      invalidatesTags: [TagTypesEnum.Auth],
    }),
    sso: build.query<ResponseDataInterface<LoginSSOResponseInterface>, LoginProviderEnum>({
      query: (provider) => ({
        method: 'GET',
        url: 'sso',
        params: { provider },
      }),
    }),
    callback: build.mutation<ResponseDataInterface<LoginResponseInterface>, LoginSSOCallbackRequestInterface>({
      query: (credentials) => ({
        method: 'POST',
        url: 'token',
        body: credentials,
      }),
    }),
    resetPassword: build.mutation<ResponseDataInterface<true>, ResetPasswordInterface>({
      query: (credentials) => ({
        method: 'POST',
        url: 'reset-password',
        body: credentials,
      }),
    }),
    newPassword: build.mutation<ResponseDataInterface<true>, NewPasswordInterface>({
      query: (credentials) => ({
        method: 'PUT',
        url: 'reset-password',
        body: credentials,
      }),
    }),
    loginMfa: build.mutation<ResponseDataInterface<LoginResponseInterface>, LoginMFARequestInterface>({
      query: (credentials) => ({
        method: 'POST',
        url: 'mfa',
        body: credentials,
      }),
      invalidatesTags: [TagTypesEnum.Auth],
    }),
    verifyUser: build.mutation<ResponseDataInterface<boolean>, string>({
      query: (key) => ({
        method: 'POST',
        url: `verify/email/${key}`,
      }),
    }),
    resendVerify: build.mutation<ResponseDataInterface<boolean>, undefined>({
      query: () => ({
        method: 'POST',
        url: 'resend/email',
      }),
    }),
  }),
});
